import RepostFilledIcon from "../../../../assets/icons/RepostFilledIcon";
import RepostGrayIcon from "../../../../assets/icons/RepostGrayIcon";
import { STATUS } from "../../../../app/constants";
import { useLoginModalContext } from "../../../../app/context/LoginModal";
import { useNotification } from "../../../../app/context/Notification";
import CustomModal from "../../../common/CustomModal/CustomModal";
import gluedin from "gluedin-shorts-js";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const feedModule = new gluedin.GluedInFeedModule();

const getRepostPayloadData = (video: any) => {
  const parsedAspectRatio = Number(video?.aspectRatio);
  const aspectRatio =
    Number.isFinite(parsedAspectRatio) && parsedAspectRatio > 0
      ? parsedAspectRatio
      : undefined;
  const templateType =
    typeof video?.templateType === "string" && video.templateType.length > 0
      ? video.templateType
      : undefined;

  return {
    userId: video.userId,
    contentType: video.contentType,
    taggedUsers: video.taggedUsers,
    s3Url: video.url,
    contentUrl: video.contentUrls,
    title: video.title,
    videoCoverImage: video.thumbnailUrl,
    thumbnailUrl: video.thumbnailUrl,
    description: video.description,
    follow: false,
    type: "repost",
    categoryId: video.categoryId,
    categoryName: video.categoryId,
    soundId: video.soundId,
    filterId: "",
    filterDisplayName: "",
    metaKeys: "",
    businessId: "",
    projectId: "",
    loc: [],
    lang: [],
    labels: [],
    question: "",
    options: [
      {
        title: "",
      },
    ],
    localisedTitle: video.localisedTitle,
    localisedDescription: video.localisedDescription,
    repost: true,
    originalVideoId: video.videoId,
    commentEnabled: video.commentEnabled,
    likeEnabled: video.likeEnabled,
    shareEnabled: video.shareEnabled,
    soundName: video.soundName,
    ...(templateType ? { templateType } : {}),
    ...(aspectRatio ? { aspectRatio } : {}),
  };
};

const getRepostMediaType = (video: any) => {
  const contentType = (
    video?.contentType ||
    video?.primaryMedia?.type ||
    ""
  ).toLowerCase();
  return contentType === "image" ? "image" : "video";
};

const pickRepostSourceUrl = (video: any, mediaType: "video" | "image") => {
  const contentUrls = Array.isArray(video?.contentUrls)
    ? video.contentUrls
    : [];
  const entry = contentUrls.find(
    (item: any) => Array.isArray(item?.urls) && item.urls.length > 0
  );
  const contentUrl = entry?.urls?.[0] || "";

  if (mediaType === "image") {
    return contentUrl || video?.thumbnailUrl || video?.url || "";
  }

  return video?.url || contentUrl || "";
};

export default function RepostModal({ show, onHide, video }: any) {
  const { setShowLoginModal } = useLoginModalContext();
  const navigate = useNavigate();
  const { repost, repostCount } = video;
  const repostMediaType = getRepostMediaType(video);
  const repostMediaLabel = repostMediaType === "image" ? "Image" : "Video";
  const alreadyRepostedMessage = `${repostMediaLabel} already reposted`;
  const repostSuccessMessage = `${repostMediaLabel} reposted successfully`;
  const [repostCounts, setRepostCounts] = useState(repostCount);

  const [isReposting, setIsReposting] = useState(false);
  const [isRepostingWithThoughts, setIsRepostingWithThoughts] = useState(false);
  const { showNotification } = useNotification();
  const [videoRepost, setVideoRepost] = useState(repost);

  const isLoggedin = async () => {
    const accessToken = await new gluedin.GluedInAuthModule().getAccessToken();
    if (accessToken) {
      return true;
    } else {
      return false;
    }
  };

  const incrementCount = () => {
    setRepostCounts((prev: number) => prev + 1);
  };

  const handleRepost = async () => {
    const isUserLoggedIn = await isLoggedin();
    if (isUserLoggedIn) {
      if (video.repost) {
        showNotification({
          title: "Repost",
          subTitle: alreadyRepostedMessage,
        });
        return;
      }

      setIsReposting(true);
      const repostPayloadData = getRepostPayloadData(video);
      try {
        const {
          data: { success, status },
        } = await feedModule.uploadContent(repostPayloadData);
        if (success) {
          onHide();
          setVideoRepost(true);
          incrementCount();
          showNotification({
            title: "Repost",
            subTitle: repostSuccessMessage,
          });
        } else {
          if (status === STATUS.VIDEO_ALREADY_REPOSTED) {
            showNotification({
              title: "Repost",
              subTitle: alreadyRepostedMessage,
            });
          }
        }
        setIsReposting(false);
      } catch (error) {
        console.error(error);
      }
    } else {
      setShowLoginModal(true);
    }
  };

  const handleRepostWithThoughts = async () => {
    const isUserLoggedIn = await isLoggedin();
    if (isUserLoggedIn) {
      if (video.repost || videoRepost) {
        showNotification({
          title: "Repost",
          subTitle: alreadyRepostedMessage,
        });
        return;
      } else {
        setIsRepostingWithThoughts(true);
        sessionStorage.removeItem("repostTemplateType");
        sessionStorage.removeItem("repostAspectRatio");
        const sourceUrl = pickRepostSourceUrl(video, repostMediaType);
        if (!sourceUrl) {
          showNotification({
            title: "Repost",
            subTitle: "Unable to load media for repost",
          });
          setIsRepostingWithThoughts(false);
          return;
        }
        try {
          const signedUrlResponse = await feedModule.getSignedUrl({
            type: repostMediaType,
          });
          if (signedUrlResponse.status === 200) {
            const {
              uploadUrl: signedUrl,
              url: mediaUrl,
            } = signedUrlResponse.data.result;
            const storageKey =
              repostMediaType === "image" ? "imageUrls" : "videoUrls";
            sessionStorage.setItem(storageKey, JSON.stringify([mediaUrl]));
            sessionStorage.setItem("blobUrls", JSON.stringify([sourceUrl]));
            sessionStorage.setItem("signedUrl", signedUrl);
            if (video?.templateType) {
              sessionStorage.setItem(
                "repostTemplateType",
                String(video.templateType)
              );
            }
            const aspectRatioValue = Number(video?.aspectRatio);
            if (Number.isFinite(aspectRatioValue) && aspectRatioValue > 0) {
              sessionStorage.setItem(
                "repostAspectRatio",
                String(aspectRatioValue)
              );
            }
            const query =
              repostMediaType === "image"
                ? "?repost=true&contentType=image"
                : "?repost=true";
            navigate(`/create-post-page-02${query}`);
            incrementCount();
          } else if (signedUrlResponse.status === 401) {
            localStorage.clear();
            sessionStorage.clear();
            navigate("/sign-in");
          } else {
            showNotification({
              title: "Repost",
              subTitle: "Unable to start repost",
            });
          }
        } catch (error) {
          console.error(error);
          showNotification({
            title: "Repost",
            subTitle: "Unable to start repost",
          });
        } finally {
          setIsRepostingWithThoughts(false);
        }
      }
    } else {
      setShowLoginModal(true);
    }
  };

  return (
    <>
      <CustomModal isOpen={show} close={onHide}>
        <div className="repost_actions">
          <button
            className="repost_actions-btn"
            onClick={handleRepostWithThoughts}
          >
            <div className="btn-wrap">
              <span>
                <RepostFilledIcon />
              </span>
              <div>
                {isRepostingWithThoughts
                  ? "Reposting With Thoughts..."
                  : "Repost with your thoughts"}
              </div>
            </div>
          </button>
          <button className="repost_actions-btn" onClick={handleRepost}>
            <div className="btn-wrap">
              <span>
                <RepostGrayIcon />
              </span>
              <div>{isReposting ? "Reposting..." : "Repost"}</div>
            </div>
          </button>
        </div>
      </CustomModal>
    </>
  );
}
